import { customAlphabet } from 'nanoid'
import { z } from 'zod'

export const PUBLIC_ID_ALPHABET =
  '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz' as const
export const DEFAULT_PUBLIC_ID_LENGTH = 22
export const MIN_PUBLIC_ID_LENGTH = 10
export const MAX_PUBLIC_ID_LENGTH = 64

/**
 * Registro canónico de entidades y prefijos públicos.
 *
 * Los valores son parte del contrato v2: nunca se deben reutilizar para otra
 * entidad ni derivar desde el nombre de una colección Mongo.
 */
export const ENTITY_PUBLIC_ID_PREFIXES = {
  user: 'usr',
  role: 'rol',
  client: 'cli',
  negotiation: 'neg',
  project: 'prj',
  property: 'prp',
  typeProperty: 'typ',
  state: 'sta',
  // Requisito documental del portal. Es un SUBDOCUMENTO del estado, no una
  // colección: su `atr_` identifica la fila dentro de `state.attachmentRequirements`.
  // El uuid legacy del subdocumento sobrevive porque es la join key de
  // `attachment.requirementId`; `atr_` es lo único que cruza el wire v2.
  attachmentRequirement: 'atr',
  // El ADJUNTO: un archivo colgado de un dueño, con su carpeta, su nombre visible y
  // su visibilidad (ADR 0022, capa 2). No confundir con el asset: el adjunto es el
  // USO, el asset son los bytes.
  //
  // Convive con el `id` uuid legacy en vez de reemplazarlo: v1 direcciona por ese
  // uuid en once endpoints y `folder.files[]` lo referencia. `att_` es lo único que
  // cruza el wire v2 —el interceptor de contrato rechaza un uuid crudo en `id`, y
  // hasta que este prefijo existió los cuatro endpoints de documentos respondían 500
  // apenas había una fila que devolver.
  attachment: 'att',
  // La CARPETA del cartapacio: el nodo del árbol que agrupa adjuntos (ADR 0022).
  //
  // Mismo caso que `att_` y por el mismo motivo: el árbol se arma internamente con
  // los `_id` de Mongo —`parent`, `children[]` y `files[]` enganchan por ahí— y un
  // ObjectId crudo tampoco puede cruzar el wire v2. El `_id` sigue siendo la llave
  // del árbol; `fld_` es lo único que se publica.
  folder: 'fld',
  // Un ARCHIVO en la biblioteca de medios: los bytes y su metadata técnica, sin
  // dueño ni significado de negocio (ADR 0022, capa 1). No confundir con el
  // adjunto: el mismo `ast_` puede estar publicado en muchos lugares a la vez —
  // ese es justamente el punto de separarlo.
  asset: 'ast',
  // Categoría de la plantilla del cartapacio (ADR 0022 §7.2). Su identidad es
  // ESTABLE y va separada del nombre visible a propósito: es la JOIN KEY de la
  // herencia. Si el merge se hiciera por nombre, «Planos» del proyecto y «PLANOS »
  // de la negociación serían dos carpetas en la pantalla del cliente.
  documentCategory: 'cat',
  company: 'cmp',
  agency: 'agc',
  society: 'soc',
  representative: 'rep',
  profession: 'prf',
  // El motor de flujos del portal usa `flw`. Un workflow es OTRA entidad: la
  // máquina de estados de una negociación del backoffice.
  workflow: 'wfl',
  clientAccount: 'cac',
  session: 'ses',
  personalAccessToken: 'pat',
  otpChallenge: 'otp',
  passwordRecovery: 'pwr',
  accessGrant: 'grt',
  export: 'exp',
  // Comentario del panel de Inventario. Es específico del bounded context:
  // otros módulos también tienen comentarios y no deben compartir identidad.
  inventoryComment: 'icm',
  reservation: 'rsv',
  // La ENTREGA (Postventa): el acta de entrega del inmueble al cliente, colgada
  // de una negociación (a lo sumo una por negociación).
  //
  // Reemplaza al uuid v4 crudo que traía el módulo legacy. A diferencia de `att_`
  // o `fld_`, acá el uuid NO sobrevive como join key: nada lo referencia — v1
  // direcciona por ObjectId y la negociación guarda el `_id` en `delivery`. Por eso
  // el backfill puede pisarlo en vez de convivir con él.
  delivery: 'dlv',
  approval: 'apr',
  portalFormRequest: 'pfr',
  flow: 'flw',
  // Una entrada del CENTRO DE AVISOS del cliente: el registro de que algo pasó y
  // se le contó. Es del carril `client-app`, como `cac_` y `grt_`.
  //
  // Es una entidad propia y no el id del hecho que la originó, y esa distinción
  // importa: un mismo pago registrado le genera una notificación a CADA cuenta con
  // acceso al inmueble, y cada una se lee —o no— por separado. Direccionar por el
  // id del pago haría imposible marcar como leída la de una persona sola.
  clientNotification: 'ntf',
  // El DISPOSITIVO al que se le manda push: un teléfono o tablet que concedió el
  // permiso, con el token de Expo que lo alcanza.
  //
  // La entidad es el EQUIPO y no el token, y esa es toda la diferencia: Expo rota
  // los tokens solo. Si la identidad fuera el token, cada rotación sería un
  // dispositivo nuevo y el mismo teléfono terminaría recibiendo una copia del aviso
  // por cada token que alguna vez tuvo. Quien identifica al equipo es el
  // `X-Installation-Id` que la app ya manda en todos los requests (ADR 0007).
  clientPushToken: 'pth',
  // El EXPEDIENTE de crédito: el trámite bancario de una negociación cerrada, con
  // sus 7 etapas y su checklist. Cuelga de la negociación —a lo sumo uno vivo por
  // negociación, como la entrega— y vive los 4 a 6 meses que hay entre la promesa
  // firmada y el desembolso.
  credit: 'crd',
  // La PLANTILLA del proceso: el nombre estable al que apunta la administración
  // («Proceso de crédito IDC»). NO tiene contenido propio — sus etapas viven en las
  // versiones. Es la identidad que sobrevive a cada publicación.
  creditTemplate: 'ctp',
  // Una VERSIÓN publicada de esa plantilla, y la razón por la que son dos entidades
  // y no una.
  //
  // El expediente MATERIALIZA la versión con la que nació: publicar una plantilla
  // nueva no toca los trámites en curso (misma decisión que
  // `PortalFormRequest.enabledSections`). Sin identidad propia para la versión no se
  // puede responder «¿con qué reglas se abrió este expediente?», que es justo lo que
  // se pregunta cuando una etapa vieja no coincide con la plantilla de hoy.
  creditTemplateVersion: 'ctv',
  // La ACEPTACIÓN de una versión de los textos legales por una cuenta de la app.
  //
  // Es una entidad propia y no un campo de `client_accounts` porque la prueba legal es un
  // HISTORIAL: «aceptó la 1.0 el 3 de octubre» tiene que sobrevivir a que después acepte la
  // 1.1. Un campo se pisa; una fila se agrega. La colección es append-only y su unicidad es
  // (cuenta, versión).
  legalAcceptance: 'lac'
} as const

export type PublicIdEntity = keyof typeof ENTITY_PUBLIC_ID_PREFIXES
export type PublicIdPrefix = (typeof ENTITY_PUBLIC_ID_PREFIXES)[PublicIdEntity]

const publicIdPrefixes = Object.freeze(
  Object.values(ENTITY_PUBLIC_ID_PREFIXES)
) as readonly PublicIdPrefix[]

export const PUBLIC_ID_PREFIXES: ReadonlySet<PublicIdPrefix> = new Set(publicIdPrefixes)

declare const publicIdBrand: unique symbol

/** Identificador público nominal. El parámetro evita cruces entre entidades. */
export type PublicId<Prefix extends PublicIdPrefix = PublicIdPrefix> = `${Prefix}_${string}` & {
  readonly [publicIdBrand]: Prefix
}

export type UserId = PublicId<'usr'>
export type RoleId = PublicId<'rol'>
export type ClientId = PublicId<'cli'>
export type NegotiationId = PublicId<'neg'>
export type ProjectId = PublicId<'prj'>
export type PropertyId = PublicId<'prp'>
export type TypePropertyId = PublicId<'typ'>
export type StateId = PublicId<'sta'>
export type AttachmentRequirementId = PublicId<'atr'>
export type AttachmentId = PublicId<'att'>
export type FolderId = PublicId<'fld'>
export type AssetId = PublicId<'ast'>
export type DocumentCategoryId = PublicId<'cat'>
export type CompanyId = PublicId<'cmp'>
export type AgencyId = PublicId<'agc'>
export type SocietyId = PublicId<'soc'>
export type RepresentativeId = PublicId<'rep'>
export type ProfessionId = PublicId<'prf'>
export type WorkflowId = PublicId<'wfl'>
export type ClientAccountId = PublicId<'cac'>
export type SessionId = PublicId<'ses'>
export type PersonalAccessTokenId = PublicId<'pat'>
export type OtpChallengeId = PublicId<'otp'>
export type PasswordRecoveryId = PublicId<'pwr'>
export type AccessGrantId = PublicId<'grt'>
export type ExportId = PublicId<'exp'>
export type InventoryCommentId = PublicId<'icm'>
export type ReservationId = PublicId<'rsv'>
export type DeliveryId = PublicId<'dlv'>
/** El EXPEDIENTE del portal: el pedido de validación, no la credencial que lo abre (decisión Nº 44). */
export type PortalFormRequestId = PublicId<'pfr'>
export type ClientNotificationId = PublicId<'ntf'>
export type ClientPushTokenId = PublicId<'pth'>
export type CreditId = PublicId<'crd'>
export type CreditTemplateId = PublicId<'ctp'>
export type CreditTemplateVersionId = PublicId<'ctv'>
export type LegalAcceptanceId = PublicId<'lac'>

const nanoid = customAlphabet(PUBLIC_ID_ALPHABET)
const escapedAlphabet = PUBLIC_ID_ALPHABET.replace(/[\\^$.*+?()[\]{}|/-]/g, '\\$&')
const publicIdPatternByPrefix = new Map<PublicIdPrefix, RegExp>()

function assertLength(length: number): void {
  if (
    !Number.isSafeInteger(length) ||
    length < MIN_PUBLIC_ID_LENGTH ||
    length > MAX_PUBLIC_ID_LENGTH
  ) {
    throw new RangeError(
      `Public ID length must be an integer between ${MIN_PUBLIC_ID_LENGTH} and ${MAX_PUBLIC_ID_LENGTH}`
    )
  }
}

function patternFor(prefix: PublicIdPrefix): RegExp {
  const cached = publicIdPatternByPrefix.get(prefix)
  if (cached) return cached

  const pattern = new RegExp(
    `^${prefix}_[${escapedAlphabet}]{${MIN_PUBLIC_ID_LENGTH},${MAX_PUBLIC_ID_LENGTH}}$`
  )
  publicIdPatternByPrefix.set(prefix, pattern)
  return pattern
}

export function newId<Prefix extends PublicIdPrefix>(
  prefix: Prefix,
  length = DEFAULT_PUBLIC_ID_LENGTH
): PublicId<Prefix> {
  assertLength(length)
  return `${prefix}_${nanoid(length)}` as PublicId<Prefix>
}

export function isPublicId<Prefix extends PublicIdPrefix>(
  value: unknown,
  prefix: Prefix
): value is PublicId<Prefix> {
  return typeof value === 'string' && patternFor(prefix).test(value)
}

export function publicIdSchema<Prefix extends PublicIdPrefix>(prefix: Prefix) {
  return z
    .string()
    .regex(patternFor(prefix), `Expected a ${prefix}_ public ID`)
    .transform((value) => value as PublicId<Prefix>)
}

export function parsePublicId<Prefix extends PublicIdPrefix>(
  value: unknown,
  prefix: Prefix
): PublicId<Prefix> {
  return publicIdSchema(prefix).parse(value)
}

export function publicIdPrefix(value: string): PublicIdPrefix | undefined {
  const separatorIndex = value.indexOf('_')
  if (separatorIndex < 1) return undefined

  const prefix = value.slice(0, separatorIndex) as PublicIdPrefix
  return PUBLIC_ID_PREFIXES.has(prefix) ? prefix : undefined
}

export const PublicIdSchemas = Object.freeze({
  user: publicIdSchema('usr'),
  role: publicIdSchema('rol'),
  client: publicIdSchema('cli'),
  negotiation: publicIdSchema('neg'),
  project: publicIdSchema('prj'),
  property: publicIdSchema('prp'),
  typeProperty: publicIdSchema('typ'),
  state: publicIdSchema('sta'),
  attachmentRequirement: publicIdSchema('atr'),
  attachment: publicIdSchema('att'),
  folder: publicIdSchema('fld'),
  asset: publicIdSchema('ast'),
  documentCategory: publicIdSchema('cat'),
  company: publicIdSchema('cmp'),
  agency: publicIdSchema('agc'),
  society: publicIdSchema('soc'),
  representative: publicIdSchema('rep'),
  profession: publicIdSchema('prf'),
  workflow: publicIdSchema('wfl'),
  clientAccount: publicIdSchema('cac'),
  session: publicIdSchema('ses'),
  personalAccessToken: publicIdSchema('pat'),
  otpChallenge: publicIdSchema('otp'),
  passwordRecovery: publicIdSchema('pwr'),
  accessGrant: publicIdSchema('grt'),
  export: publicIdSchema('exp'),
  inventoryComment: publicIdSchema('icm'),
  reservation: publicIdSchema('rsv'),
  delivery: publicIdSchema('dlv'),
  approval: publicIdSchema('apr'),
  portalFormRequest: publicIdSchema('pfr'),
  flow: publicIdSchema('flw'),
  clientNotification: publicIdSchema('ntf'),
  clientPushToken: publicIdSchema('pth'),
  credit: publicIdSchema('crd'),
  creditTemplate: publicIdSchema('ctp'),
  creditTemplateVersion: publicIdSchema('ctv'),
  legalAcceptance: publicIdSchema('lac')
})
